/* eslint-disable @typescript-eslint/restrict-template-expressions */
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import numeral from 'numeral'
import React, { useEffect } from 'react'
import { CardProps } from 'src/common/components/Card'
import { Lane } from 'src/common/components/Lane'
import { JoinRaceLanesSection } from 'src/common/components/Race/JoinRaceLanesSection'
import { RaceDetailContent } from 'src/common/components/Race/RaceDetailContent'
import { RaceDetailHero } from 'src/common/components/Race/RaceDetailHero'
import { RaceProductDetail } from 'src/common/components/Race/RaceProductDetail'
import { Tabs } from 'src/common/components/Tabs'
import { trackerCards } from 'src/common/constants/trackerCards'
import { faqCards } from 'src/common/constants/faqCards'
import { useAthlete } from 'src/common/hooks/useAthlete'
import { useUser } from 'src/common/hooks/useUser'
import { trackView } from 'src/common/lib/segment'
import { ViewEventProps } from 'src/common/lib/segment/types'
import { prependAssetHost, prependAssetHostChallenge } from 'src/common/utils'
import { getBadgeType } from 'src/common/utils/cards'
import { getEventById, getRelatedRaces } from 'src/services/mainAPI'
import { ChallengeDiscoveryRes } from 'src/services/types/challengeDiscovery'
import { JoinEventRes } from 'src/services/types/joinEventV2'
import { wrapper } from 'src/store'
import { useGetSingleRewardV2 } from 'src/store/joinevent/selectors'
import GoogleStaticMap from '../../common/components/GoogleStaticMap'
import { AppLayout } from '../../common/components/Layout/AppLayout'

const RaceDetail = ({
    data,
    relatedRaces
}: {
    data: JoinEventRes
    relatedRaces: ChallengeDiscoveryRes
}) => {
    const user = useUser()
    const router = useRouter()
    const { data: athlete } = useAthlete()
    const isAlreadyJoined = data.cart.challengeAthleteId !== null
    const { id } = router.query
    const challengeId = id[0]
    const item = useGetSingleRewardV2(id[2] ? Number(id[2]) : null)

    useEffect(() => {
        const eventProps: ViewEventProps = {
            productData: data,
            user: user
        }

        trackView(eventProps)
    }, [id])

    useEffect(() => {
        // sad face
        setTimeout(() => {
            if (typeof window !== 'undefined') {
                // Get the hash from the url
                const hashId = window.location.hash

                if (hashId) {
                    // Use the hash to find the first element with that id
                    const element = document.querySelector(hashId)

                    if (element) {
                        // Smooth scroll to that elment
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest'
                        })
                    }
                }
            }
        }, 500)
    }, [router.asPath])
    const renderContent = (tabId: string) => {
        if (isAlreadyJoined) {
            switch (tabId) {
                case 'rewards':
                    return (
                        <JoinRaceLanesSection
                            isAddonOptional={data.summary.isAddonOptional}
                            isAddonOptionalReason={data.summary.isAddonOptionalReason}
                            challengeId={challengeId}
                            isAlreadyJoined={data.cart.challengeAthleteId !== null}
                            isLoggedIn={user ? true : false}
                        />
                    )
                case 'about':
                    return (
                        <div className="max-w-screen-fullHd mx-auto">
                            <RaceDetailContent
                                htmlString={data?.summary?.fundraisingMessage.replace(
                                    /(<|&lt;)br\s*\/*(>|&gt;)/g,
                                    ''
                                )}
                                video={
                                    data.summary.introVideoUrl
                                        ? {
                                              videoThumbUrl: prependAssetHostChallenge(
                                                  data.summary.imageUrl
                                              ),
                                              videoUrl: data.summary.introVideoUrl
                                          }
                                        : null
                                }
                                metadata={{
                                    completeBy: data.summary.endDate
                                        ? new Date(data.summary.endDate).toDateString()
                                        : 'N/A',
                                    finish: 'At Your Own Pace',
                                    participateFrom: 'Anywhere',
                                    spotifyPlaylist: '',
                                    suitableFor: 'All Ages'
                                }}
                                isHowFarCanYouGo={data.summary.isHowFarCanYouGo}
                                stats={{
                                    milesLogged:
                                        data?.summary?.challengeData
                                            ?.totalDistanceLoggedString,
                                    participants:
                                        data?.summary?.challengeData?.athleteCountString,
                                    caloriesBurned:
                                        data?.summary?.challengeData
                                            ?.totalCaloriesLoggedString
                                }}
                            />

                            <div className="mb-2 md:mb-10" />

                            {!data?.summary?.isGameMap && (
                                <div className="p-6">
                                    <p className="text-xl md:text-3xl text-gray-800 font-medium mb-4">
                                        Exercise from Anywhere & See Your Progress on This
                                        Route
                                    </p>
                                    <div>
                                        <GoogleStaticMap
                                            polyline={data.geography.encodedPolyline}
                                        />
                                    </div>
                                </div>
                            )}

                            <Lane
                                titleIconComponent={() => (
                                    <i
                                        aria-hidden
                                        className="fas fa-question-circle text-gray-600 text-3xl leading-none"
                                    ></i>
                                )}
                                title="FAQ"
                                seeAllHref="https://help.yes.fit"
                                description="New to Yes.Fit? Check out some of our helpful links to get you started on your journey to a better you!"
                                data={faqCards.map((faq) => {
                                    return {
                                        cardType: 'faq',
                                        imageURL: faq.imageURL,
                                        title: faq.title,
                                        cardURL: faq.cardURL
                                    }
                                })}
                            />

                            <Lane
                                title="Related Races"
                                data={
                                    relatedRaces.challengeDiscovery?.groups[0]?.challenges
                                        ?.slice(0, 14)
                                        .map((item): CardProps => {
                                            return {
                                                cardType: 'race',
                                                badgeType: getBadgeType(item, user),
                                                distance: `${item.totalDistance} ${item.distanceUnit}`,
                                                imageURL: item.logo
                                                    ? prependAssetHostChallenge(item.logo)
                                                    : prependAssetHost(
                                                          '/icons/no-race-bg.png'
                                                      ),
                                                title: item.name,
                                                subtitle: athlete?.stripePlanId
                                                    ? null
                                                    : `Starting at $${item.minimumPrice}`,
                                                href: `/races/${encodeURIComponent(
                                                    item.challengeUrl
                                                )}`
                                            }
                                        }) ?? []
                                }
                            />
                        </div>
                    )
                default:
                    return (
                        <>
                            <div className="flex flex-col px-5 md:px-0 justify-center items-center">
                                <div className="mt-4">
                                    <Image
                                        src="/enable-location.svg"
                                        width={228}
                                        height={164}
                                    />
                                </div>
                                <h1 className="text-xl md:text-3xl font-medium mb-2">
                                    New Feature Coming Soon!
                                </h1>
                                <p className="text-gray-600 text-sm md:text-lg text-center">
                                    Head over to the old Dashboard to view your Progress
                                    for this Race.
                                </p>
                                <Link href="/dashboard">
                                    <button className="bg-yf-purple flex items-center justify-center w-full py-4 rounded-lg my-4 cursor-pointer text-white font-medium px-4 text-center max-w-[250px]">
                                        Go to Old Dashboard
                                    </button>
                                </Link>
                            </div>
                        </>
                    )
            }
        } else {
            return (
                <div className="max-w-screen-fullHd mx-auto">
                    <section className="bg-gradient-to-b from-[#EFF5F4] to-[#f2f1ff] mb-2">
                        <div className="px-4 py-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8 lg:py-6">
                            <div className="grid gap-8 row-gap-8 grid-cols-2 lg:grid-cols-4">
                                <div className="relative text-center">
                                    <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full border-3 border-yf-purple mx-auto ">
                                        <img
                                            alt="Yes.Fit Sign Up"
                                            src="https://res.cloudinary.com/yes-fit/image/upload/q_auto/v1638821593/manual/medal-icon.png"
                                            className="w-8 h-8 text-white"
                                        />
                                    </div>
                                    <h6 className="mb-2 text-xl font-medium leading-5">
                                        Earn Real Rewards &<br />
                                        Add-Ons
                                    </h6>
                                </div>
                                <div className="relative text-center">
                                    <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full border-3 border-yf-purple mx-auto ">
                                        <img
                                            alt="Yes.Fit Sign Up"
                                            src="https://res.cloudinary.com/yes-fit/image/upload/q_auto/v1638821593/manual/map-pin-icon.png"
                                            className="w-8 h-8 text-white"
                                        />
                                    </div>
                                    <h6 className="mb-2 text-xl font-medium leading-5">
                                        Track Your Progress
                                        <br />
                                        (inside our apps)
                                    </h6>
                                </div>
                                <div className="relative text-center">
                                    <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full border-3 border-yf-purple mx-auto ">
                                        <img
                                            alt="Yes.Fit Sign Up"
                                            src="https://res.cloudinary.com/yes-fit/image/upload/q_auto/v1638821593/manual/travel-icon.png"
                                            className="w-8 h-8 text-white"
                                        />
                                    </div>
                                    <h6 className="mb-2 text-xl font-medium leading-5">
                                        Travel the World
                                        <br />
                                        (Virtually)
                                    </h6>
                                </div>
                                <div className="relative text-center">
                                    <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full border-3 border-yf-purple mx-auto ">
                                        <img
                                            alt="Yes.Fit Sign Up"
                                            src="https://res.cloudinary.com/yes-fit/image/upload/q_auto/v1638821593/manual/smartwatch-icon.png"
                                            className="w-8 h-8 text-white"
                                        />
                                    </div>
                                    <h6 className="mb-2 text-xl font-medium leading-5">
                                        Sync With Your Favorite Health Apps
                                    </h6>
                                </div>
                            </div>
                        </div>
                    </section>
                    <RaceDetailContent
                        htmlString={data?.summary?.fundraisingMessage.replace(
                            /(<|&lt;)br\s*\/*(>|&gt;)/g,
                            ''
                        )}
                        metadata={{
                            completeBy: data.summary.endDate
                                ? new Date(data.summary.endDate).toDateString()
                                : 'N/A',
                            finish: 'At Your Own Pace',
                            participateFrom: 'Anywhere',
                            spotifyPlaylist: '',
                            suitableFor: 'All Ages'
                        }}
                        video={
                            data.summary.introVideoUrl
                                ? {
                                      videoThumbUrl: prependAssetHostChallenge(
                                          data.summary.imageUrl
                                      ),
                                      videoUrl: data.summary.introVideoUrl
                                  }
                                : null
                        }
                        isHowFarCanYouGo={data.summary.isHowFarCanYouGo}
                        stats={{
                            milesLogged:
                                data?.summary?.challengeData?.totalDistanceLoggedString,
                            participants:
                                data?.summary?.challengeData?.athleteCountString,
                            caloriesBurned:
                                data?.summary?.challengeData?.totalCaloriesLoggedString
                        }}
                    />

                    <JoinRaceLanesSection
                        isAddonOptional={data.summary.isAddonOptional}
                        isAddonOptionalReason={data.summary.isAddonOptionalReason}
                        challengeId={challengeId}
                        isAlreadyJoined={data.cart.challengeAthleteId !== null}
                        isLoggedIn={user ? true : false}
                        onButtonPress={
                            () => {
                                const element = window.document.getElementById('rewards')
                                element.scrollIntoView()
                            }

                            // void router.push(`/races/${challengeId}#rewards`, null, {
                            //     shallow: true
                            // })
                        }
                    />
                    <div className="mb-0 md:mb-10" />

                    {!data?.summary?.isGameMap && (
                        <div className="p-4 md:p-14">
                            <p className="text-xl md:text-3xl text-gray-800 font-medium mb-4">
                                Exercise from Anywhere & See Your Progress on This Route
                            </p>
                            <div>
                                <GoogleStaticMap
                                    polyline={data.geography.encodedPolyline}
                                />
                            </div>
                        </div>
                    )}

                    <div className="mb-6 md:mb-10" />
                    <section className="md:pb-10 pl-2 md:pl-0">
                        <Lane
                            titleIconComponent={() => (
                                <i
                                    aria-hidden
                                    className="fas fa-question-circle text-gray-600 text-3xl leading-none"
                                ></i>
                            )}
                            title="FAQ"
                            seeAllHref="https://help.yes.fit"
                            description="New to Yes.Fit? Check out some of our helpful links to get you started on your journey to a better you!"
                            data={faqCards.map((faq) => {
                                return {
                                    cardType: 'faq',
                                    imageURL: faq.imageURL,
                                    title: faq.title,
                                    cardURL: faq.cardURL
                                }
                            })}
                        />
                    </section>
                    <section className="md:pb-10 pl-3 md:pl-0">
                        <Lane
                            title="Fitness Trackers"
                            titleIconComponent={() => (
                                <i
                                    aria-hidden
                                    style={{ color: 'rgb(250, 187, 4)' }}
                                    className="fas fa-running text-3xl leading-none"
                                ></i>
                            )}
                            description="Use our integrated fitness tracker or sync your own! We support a wide range of fitness trackers to keep your data all in one place."
                            data={trackerCards.map((tracker) => {
                                return {
                                    cardType: 'tracker',
                                    imageURL: tracker.imageURL,
                                    title: tracker.title
                                }
                            })}
                        />
                    </section>
                    <section className="pl-2 md:pl-0">
                        <Lane
                            title="Related Races"
                            data={
                                relatedRaces.challengeDiscovery?.groups[0]?.challenges
                                    ?.slice(0, 14)
                                    .map((item): CardProps => {
                                        return {
                                            cardType: 'race',
                                            // badgeType: getBadgeType(item, user),
                                            distance: `${item.totalDistance} ${item.distanceUnit}`,
                                            imageURL: item.logo
                                                ? prependAssetHostChallenge(item.logo)
                                                : prependAssetHost(
                                                      '/icons/no-race-bg.png'
                                                  ),
                                            title: item.name,
                                            subtitle: athlete?.stripePlanId
                                                ? null
                                                : `Starting at $${item.minimumPrice}`,
                                            href: `/races/${encodeURIComponent(
                                                item.challengeUrl
                                            )}`
                                        }
                                    }) ?? []
                            }
                        />
                    </section>
                </div>
            )
        }
    }

    return (
        <AppLayout
            pageTitle={data?.summary?.name}
            metaTitle={`${data?.summary?.name} | Virtual Races | Yes.Fit`}
            metaDescription={data?.summary?.metaDescription}
            metaKeywords={data?.summary?.metaKeywords}
            metaImage={prependAssetHostChallenge(data.summary.imageUrl)}
        >
            <div>
                <div className=" 2xl:max-w-screen-fullHd mx-auto">
                    {id.length === 1 || id.length === 2 ? (
                        <>
                            <RaceDetailHero
                                video={
                                    data.summary.introVideoUrl
                                        ? {
                                              videoThumbUrl: prependAssetHostChallenge(
                                                  data.summary.imageUrl
                                              ),
                                              videoUrl: data.summary.introVideoUrl
                                          }
                                        : null
                                }
                                mainImage={
                                    data.summary.imageUrl
                                        ? prependAssetHostChallenge(data.summary.imageUrl)
                                        : ''
                                }
                                title={data.summary.name}
                                distanceText={`${numeral(
                                    data.summary.totalDistance
                                ).format('0,0.[0]')} Miles`}
                                isMember={athlete?.stripePlanId ? true : false}
                                minPrice={data.summary.minimumPrice}
                                buttonText="Join Race"
                                onButtonPress={() => {
                                    const element =
                                        window.document.getElementById('rewards')
                                    element.scrollIntoView()
                                }}
                                onSharePress={() => console.log('share press')}
                                isAlreadyJoined={isAlreadyJoined}
                            />
                            <div className="bg-white max-w-screen-fullHd mx-auto mb-4">
                                <Tabs
                                    items={
                                        isAlreadyJoined
                                            ? [
                                                  {
                                                      href: `/races/${encodeURIComponent(
                                                          challengeId
                                                      )}`,
                                                      isActive: id.length === 1,
                                                      text: 'Progress'
                                                  },
                                                  {
                                                      href: `/races/${encodeURIComponent(
                                                          challengeId
                                                      )}/rewards`,
                                                      isActive: id[1] === 'rewards',
                                                      text: 'Rewards'
                                                  },
                                                  {
                                                      href: `/races/${encodeURIComponent(
                                                          challengeId
                                                      )}/about`,
                                                      isActive: id[1] === 'about',
                                                      text: 'About'
                                                  }
                                              ]
                                            : [
                                                  {
                                                      href: `/races/${encodeURIComponent(
                                                          challengeId
                                                      )}`,
                                                      isActive: id.length === 1,
                                                      text: 'About'
                                                  }
                                              ]
                                    }
                                />
                            </div>
                            {renderContent(id[1])}
                        </>
                    ) : null}

                    {id.length === 3 && item ? (
                        <RaceProductDetail
                            challengeId={challengeId}
                            currentItem={item}
                            isAlreadyJoined={isAlreadyJoined}
                        />
                    ) : null}
                </div>
            </div>
        </AppLayout>
    )
}

export const getServerSideProps = wrapper.getServerSideProps(
    (store) => async (context) => {
        const { id } = context.params
        const raceId = id[0]

        // only support 1 sub path e.g /race/subpath
        if (id.length > 3) {
            return {
                notFound: true
            }
        }

        const { data, error } = await store.dispatch(getEventById.initiate(raceId))
        const newData = {
            ...data,
            summary: {
                ...data.summary,
                googleDirectionsResult: ''
            }
        }

        if (error) {
            console.log({ error })
            return {
                redirect: {
                    destination: '/races',
                    permanent: false
                }
            }
        }

        if (!data) {
            return {
                notFound: true
            }
        }

        if (data.summary.is30Day) {
            return {
                redirect: {
                    destination: `/challenges/${raceId}`,
                    permanent: true
                }
            }
        }

        if (data.summary.isSwag) {
            return {
                redirect: {
                    destination: `/store/${raceId}`,
                    permanent: true
                }
            }
        }

        const { data: relatedRaces } = await store.dispatch(
            getRelatedRaces.initiate(data.summary.challengeId)
        )

        return { props: { data: newData, relatedRaces } }
    }
)

export default RaceDetail
